/* eslint-disable no-magic-numbers */
import React, { useRef, useState } from 'react';
import { glyphsDataSelector } from '@/redux/glyphs/glyphs.selectors';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { Input } from '@/shared/Input';
import { Glyph } from '@/types/models';
import { Autocomplete } from '@/shared/Autocomplete';
import { addGlyph, getGlyphs } from '@/redux/glyphs/glyphs.thunks';
import {
  getModelElementsForModel,
  updateElementGlyph,
} from '@/redux/modelElements/modelElements.thunks';
import { showToast } from '@/utils/showToast';
import { SerializedError } from '@reduxjs/toolkit';
import { elementGlyphStateSelector } from '@/redux/modal/modal.selector';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { LoadingIndicator } from '@/shared/LoadingIndicator';

const LOADING_INDICATOR_SIZE = 44;

type ElementImageFormProps = {
  selectedGlyph: number | null;
  setSelectedGlyph: (glyphId: number | null) => void;
};

export const ElementImageForm: React.FC<ElementImageFormProps> = ({
  selectedGlyph,
  setSelectedGlyph,
}) => {
  const glyphs: Glyph[] = useAppSelector(glyphsDataSelector);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initialSelectedGlyph = glyphs.find(glyph => glyph.id === selectedGlyph);

  const elementGlyphState = useAppSelector(elementGlyphStateSelector);

  const dispatch = useAppDispatch();

  const [isSending, setIsSending] = useState<boolean>(false);

  const saveGlyph = async (glyphId: number | null): Promise<void> => {
    if (!elementGlyphState) {
      return;
    }
    setIsSending(true);
    try {
      await dispatch(
        updateElementGlyph({
          modelId: elementGlyphState.model,
          elementId: elementGlyphState.id,
          glyphId,
        }),
      ).unwrap();
      showToast({
        type: 'success',
        message: 'The element glyph has been successfully updated.',
      });
      dispatch(getModelElementsForModel(elementGlyphState.model));
    } catch (error) {
      const typedError = error as SerializedError;
      showToast({
        type: 'error',
        message: typedError.message || 'An error occurred while updating a glyph.',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleGlyphChange = (glyph: Glyph | null): void => {
    const glyphId = glyph?.id || null;
    setSelectedGlyph(glyphId);
    if (!glyphId && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    saveGlyph(glyphId);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const uploadedFile = e.target.files?.[0] || null;
    if (!uploadedFile) {
      return;
    }
    setSelectedGlyph(null);
    setIsSending(true);
    try {
      const data = await dispatch(addGlyph(uploadedFile)).unwrap();
      if (!data) {
        return;
      }
      dispatch(getGlyphs());
      setSelectedGlyph(data.id);
      await saveGlyph(data.id);
    } catch (error) {
      const typedError = error as SerializedError;
      showToast({
        type: 'error',
        message: typedError.message || 'An error occurred while uploading a glyph.',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div>
      {isSending && (
        <div className="c-edit-element-modal-loader">
          <LoadingIndicator width={LOADING_INDICATOR_SIZE} height={LOADING_INDICATOR_SIZE} />
        </div>
      )}
      <div className="grid grid-cols-3 gap-x-3 gap-y-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-font-500">Glyph</span>
          <Autocomplete<Glyph>
            options={glyphs}
            initialValue={initialSelectedGlyph}
            clearable
            valueKey="id"
            labelKey="filename"
            onChange={handleGlyphChange}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-font-500">File</span>
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            data-testid="image-file-input"
            className="w-full border border-[#ccc] bg-white p-2"
          />
        </div>
      </div>
    </div>
  );
};
